<template>
  <d-container fluid :class="{ 'main-content-container px-4': true, 'submit-in-progress': submitInProgress }">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="4" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('purchase.ordersFromSuppliers') }}</span>
        <h3 class="page-title">
          <template v-if="isEditMode">{{ $t('purchase.editOrder') }}</template>
          <template v-else>{{ $t('purchase.addOrder') }}</template>
        </h3>
      </d-col>
      <d-col col sm="8" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          class="mr-2"
          @click.prevent="$router.push(backToReservations ? '/zakupy/rezerwacje' : '/zakupy')">
          <i class="material-icons">arrow_back</i>
         {{ $t('ui.backToList') }}
        </d-button>

        <d-button
          v-if="!isReadOnly"
          theme="success"
          @click.prevent="validateForm('normal')">
          <div
            v-if="submitInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          <template v-else>
            <i class="material-icons">
              <template v-if="isEditMode">edit</template>
              <template v-else>add</template>
            </i>
            <template v-if="isEditMode">{{ $t('purchase.saveOrder') }}</template>
            <template v-else>{{ $t('purchase.addOrder') }}</template>
          </template>
        </d-button>

        <d-button
          v-if="isEditMode && download_label_url"
          theme="info"
          class="ml-2"
          @click.prevent="downloadLabel()">
          <div
            v-if="downloadInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          <template v-else>
            <i class="material-icons">
              cloud_download
            </i>
            {{ $t('order.warehouse.boxes.downloadLabel') }}
          </template>
        </d-button>

        <d-button
          v-if="isEditMode && status !== 'CLOSED'"
          theme="warning"
          @click.prevent="validateForm('close')"
          :disabled="closeInProgress"
          class="ml-2">
          <template v-if="!closeInProgress">
            <i class="material-icons">lock</i>
            {{ $t('purchase.closeOrder') }}
          </template>
          <div
            v-if="closeInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
        </d-button>

        <d-button
          v-if="isEditMode && status !== 'CLOSED'"
          theme="danger"
          @click.prevent="validateForm('forceClose')"
          :disabled="forceCloseInProgress"
          class="ml-2">
          <template v-if="!forceCloseInProgress">
            <i class="material-icons">error</i>
            {{ $t('purchase.forceOrderClose') }}
          </template>
          <div
            v-if="forceCloseInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
        </d-button>

        <d-dropdown
          v-if="!isReadOnly && isEditMode && can_send_invoice_email && (customer_id || sub_customer_id)"
          icon="mail"
          theme="primary"
          :disabled="sendPurchaseEmailInProgress"
          class="ml-2"
          :text="$t('purchase.sendPurchaseEmail')">
          <d-dropdown-item v-if="customer_id">
            <a
              :disabled="sendPurchaseEmailInProgress !== false"
              @click.prevent="sendPurchaseEmail('customer')"
              :style="'display: block;' + ((sendPurchaseEmailInProgress !== false) ? 'pointer-events: none;' : '')">
              {{ $t('purchase.sendPurchaseEmailCustomer') }}
            </a>
          </d-dropdown-item>
          <d-dropdown-item v-if="sub_customer_id">
            <a
              :disabled="sendPurchaseEmailInProgress !== false"
              @click.prevent="sendPurchaseEmail('subcustomer')"
              :style="'display: block;' + ((sendPurchaseEmailInProgress !== false) ? 'pointer-events: none;' : '')">
              {{ $t('purchase.sendPurchaseEmailSubCustomer') }}
            </a>
          </d-dropdown-item>
        </d-dropdown>
      </d-col>
    </d-row>

    <d-alert
      v-if="parentPurchaseReset && !($refs['purchase-relations'] && $refs['purchase-relations'].parent_id)"
      show
      theme="warning">
      {{ $t('purchase.parentPurchaseRemoved') }}
    </d-alert>

    <!-- Form -->
    <d-form>
      <validation-observer ref="purchase-form" tag="form">
        <d-card class="mb-5">
          <d-tabs v-sticky-tabs
            v-if="!loadError"
            card
            :nav-class="allDataLoaded ? validationResults : ''">
            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom d-flex">
                {{ $t('ui.generalInfo') }}

                <div class="ml-auto order-status-wrapper">
                  <purchase-status
                    v-if="isEditMode && !purchaseSetUpInProgress"
                    :deadlineIsSet="deadlineIsSet"
                    :initialStatus="status"
                    :readOnly="isReadOnly" />
                </div>
              </h4>

              <d-row>
                <d-col
                  cols="12"
                  class="d-flex align-middle">
                  <span
                    v-if="exclude_from_erp_integration"
                    class="text-danger">
                    {{ $t('order.erp.isExcluded') }}
                  </span>

                  <d-badge
                    v-if="!exclude_from_erp_integration"
                    :theme="erp_optima_updated_at ? 'success' : 'danger'"
                    class="mr-3"
                    :outline="true"
                    size="lg"
                    @click.native="openErpIntegrationPopup"
                    style="cursor: pointer; font-size: 18px;">
                    <template v-if="erp_optima_updated_at">
                      {{ $t('order.erp.integratedWithErp') }}
                    </template>
                    <template v-else>
                      {{ $t('order.erp.notIntegratedWithErp') }}
                    </template>
                  </d-badge>

                  <d-button
                    v-if="!exclude_from_erp_integration && userIsAdmin"
                    theme="danger"
                    @click.prevent="excludeFromERP">
                    <div
                      v-if="excludeFromErpInProgress"
                      class="spinner-border spinner-border-sm" role="status"></div>
                    {{ $t('order.erp.excludeFrom') }}
                  </d-button>

                  <d-button
                    v-if="exclude_from_erp_integration && userIsAdmin"
                    theme="success"
                    class="ml-3"
                    @click.prevent="includeToERP">
                    <div
                      v-if="includeToErpInProgress"
                      class="spinner-border spinner-border-sm" role="status"></div>
                    {{ $t('order.erp.includeTo') }}
                  </d-button>
                </d-col>
              </d-row>

              <hr>

              <d-row v-if="!isEditMode || (isEditMode && orders_count === 0)">
                <d-col cols="12" class="mb-2">
                  <div class="form-group">
                    <label for="order_name">{{ $t('ui.orderName') }}:</label>
                    <d-input
                      :readonly="isReadOnly"
                      v-model="order_name" />
                  </div>
                </d-col>
              </d-row>

              <d-row v-if="isEditMode">
                <d-col cols="12" class="mb-2">
                  <strong>{{ $t('purchase.erpNumber') }}:</strong>
                  <template v-if="erp_number">
                    <strong class="text-primary">
                      {{ erp_number }}
                    </strong>
                  </template>
                  <template v-else>
                    &mdash;
                  </template>
                </d-col>

                <d-col cols="12" class="mb-2">
                  <strong>{{ $t('purchase.pzFor') }}:</strong>
                  <template v-if="pz_for">
                    <strong
                      v-if="pz_for === 'customer'"
                      class="text-primary">
                      {{ $t('ui.supplier')}}
                    </strong>
                    <strong
                      v-if="pz_for === 'sub_customer'"
                      class="text-primary">
                      {{ $t('ui.subSupplier')}}
                    </strong>
                  </template>
                  <template v-else>
                    &mdash;
                  </template>
                </d-col>
              </d-row>

              <d-row v-if="created_at">
                <d-col :cols="received_at || suborder_received_at ? 6 : 12" class="mb-2">
                  <p>
                    {{ $t('ui.created') }}: <strong class="text-dark">{{ created_at }}</strong><br>

                    <template v-if="sewing_room_synchronized_at">
                      {{ $t('purchase.syncDate') }}: <strong class="text-dark">{{ sewing_room_synchronized_at }}</strong><br>
                    </template>

                    <template v-if="sewing_room_status">
                      {{ $t('purchase.statusInSewingRoom') }}:
                      <span :class="'text-' + sewingRoomStatus[sewing_room_status].style">
                        {{ sewingRoomStatus[sewing_room_status].text[prefferedLanguage] }}
                      </span><br>
                    </template>

                    <template v-if="sewing_room_url && sewing_room_id">
                      <a :href="sewing_room_url + '/zamowienia/edycja/' + parseInt(sewing_room_id, 10)" target="_blank" rel="noopener noreferrer">
                        ({{ $t('purchase.seeOrderInSewingRoom') }})
                      </a><br>
                    </template>
                    <template v-else-if="sewing_room_id">
                      {{ $t('purchase.orderIDInSewing') }}: <span class="text-primary">{{ sewing_room_id }}</span>
                    </template>
                  </p>

                  <div
                    v-if="collections && collections.length"
                    class="purchase-info">
                    <div class="purchase-info-content">
                      <span
                        v-for="(collectionItem, index) of collections"
                        :key="'collection-item-' + index">
                        {{ $t('purchase.productsReceivedDate') }} <strong class="text-primary">{{ collectionItem.created_at }}</strong>
                        {{ $t('purchase.productsReceivedInOrder') }} <strong class="text-primary">{{ collectionItem.order_name }}</strong>
                      </span>
                    </div>
                  </div>
                </d-col>

                <d-col
                  v-if="received_at || suborder_received_at || sub_sewing_room_synchronized_at"
                  cols="6"
                  class="mb-2">
                  <p>
                    <template v-if="received_at">
                      {{ $t('purchase.receivedAt') }}: <strong class="text-dark">{{ received_at }}</strong>
                    </template>

                    <br v-if="received_at && suborder_received_at">

                    <template v-if="suborder_received_at">
                      {{ $t('purchase.suborderReceivedAt') }}: <strong class="text-dark">{{ suborder_received_at }}</strong>
                    </template>

                    <br v-if="sub_sewing_room_synchronized_at">

                    <template v-if="sub_sewing_room_synchronized_at">
                      {{ $t('purchase.syncDateSubSewing') }}: <strong class="text-dark">{{ sewing_room_synchronized_at }}</strong><br>
                    </template>

                    <template v-if="sub_sewing_room_status">
                      {{ $t('purchase.statusInSubSewingRoom') }}:
                      <span :class="'text-' + sewingRoomStatus[sub_sewing_room_status].style">
                        {{ sewingRoomStatus[sub_sewing_room_status].text[prefferedLanguage] }}
                      </span><br>
                    </template>

                    <template v-if="sub_sewing_room_url && sub_sewing_room_id">
                      <a :href="sub_sewing_room_url + '/zamowienia/edycja/' + parseInt(sub_sewing_room_id, 10)" target="_blank" rel="noopener noreferrer">
                        ({{ $t('purchase.seeOrderInSubSewingRoom') }})
                      </a><br>
                    </template>
                    <template v-else-if="sub_sewing_room_id">
                      {{ $t('purchase.orderIDInSubSewing') }}: <span class="text-primary">{{ sub_sewing_room_id }}</span>
                    </template>
                  </p>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="9">
                  <purchase-select-shipper
                    key="purchase-shipper-field"
                    ref="purchase-shipper"
                    :customer-name="customer_name"
                    :isSub="false"
                    :read-only="isReadOnly" />
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('purchase.warehouse') }}:
                    </label>

                    <d-form-select
                      v-if="!isReadOnly"
                      v-model="warehouse"
                      :options="[{
                        text: $t('welcome.choose'),
                        value: null
                      }, {
                        text: 'MG02',
                        value: 'MG02'
                      }, {
                        text: 'SE01',
                        value: 'SE01'
                      }, {
                        text: 'DE01',
                        value: 'DE01'
                      }]" />

                    <div v-else>
                      <strong>
                        {{ warehouse }}
                      </strong>
                    </div>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="9">
                  <purchase-select-shipper
                    key="purchase-subshipper-field"
                    ref="purchase-subshipper"
                    :customer-name="sub_customer_name"
                    :isSub="true"
                    :read-only="isReadOnly" />
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('purchase.purchaseCategory') }}:
                    </label>

                    <d-form-select
                      v-if="!isReadOnly"
                      v-model="purchase_category"
                      :options="[{
                        text: $t('welcome.choose'),
                        value: null
                      }, {
                        text: $t('purchase.purchaseType.domestic'),
                        value: 'DOMESTIC'
                      }, {
                        text: $t('purchase.purchaseType.import'),
                        value: 'IMPORT'
                      }]" />

                    <div v-else>
                      <strong v-if="purchase_category === 'DOMESTIC'">
                        {{ $t('purchase.purchaseType.domestic') }}
                      </strong>

                      <strong v-if="purchase_category === 'IMPORT'">
                        {{ $t('purchase.purchaseType.import') }}
                      </strong>
                    </div>
                  </div>
                </d-col>
              </d-row>

              <purchase-select-contact
                ref="purchase-contact"
                :read-only="isReadOnly"
                :contact-name="contact_name"
                :customer-id="allDataLoaded ? this.$refs['purchase-shipper'].customer_id : 0" />

              <hr>

              <d-row>
                <d-col cols="12">
                  <label>
                    {{ $t('ui.deadlineCap') }}:
                  </label>

                  <d-datepicker
                    v-if="!isReadOnly"
                    v-model="date"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    :language="translationsForDatePicker" />

                  <span v-else>
                    {{ dateAsString }}
                  </span>
                </d-col>
              </d-row>

              <hr>

              <d-row v-if="!parent_id || (parent_id && parent)">
                <d-col cols="4">
                  <div class="form-group">
                    <label for="inputInvoice">{{ $t('purchase.invoiceFile') }}:</label>
                    <div>
                      <template v-if="parent_id && parent">
                        <a
                          v-if="parent.file && typeof parent.file === 'string'"
                          href="#"
                          @click.prevent="downloadInvoice()">
                          <template v-if="!downloadingInvoice">
                            <i class="material-icons">request_quote</i> {{ $t('purchase.downloadInvoice') }}
                          </template>
                          <template v-if="downloadingInvoice">{{ $t('ui.downloadInProgress') }}&hellip;</template>
                        </a>
                        <span v-if="parent_id && (!parent.file || typeof parent.file !== 'string')">
                          &mdash;
                        </span>
                      </template>
                      <template v-else>
                        <a
                          v-if="file && typeof file === 'string'"
                          href="#"
                          @click.prevent="downloadInvoice()">
                          <template v-if="!downloadingInvoice">
                            <i class="material-icons">request_quote</i> {{ $t('purchase.downloadInvoice') }}
                          </template>
                          <template v-if="downloadingInvoice">{{ $t('ui.downloadInProgress') }}&hellip;</template>
                        </a>
                        <span v-if="parent_id && (!file || typeof file !== 'string')">
                          &mdash;
                        </span>
                      </template>

                      <a
                        v-if="!parent_id && file && typeof file === 'string' && !isReadOnly"
                        href="#"
                        @click.prevent="removeInvoice"
                        class="text-danger ml-3">
                        <small>{{ $t('purchase.removeInvoice') }}</small>
                      </a>

                      <file-input
                        v-if="!parent_id && (!file || typeof file !== 'string') && !isReadOnly"
                        :id="'inputAttachment'"
                        :label="$t('purchase.selectInvoiceFile')"
                        :onChange="setInvoice" />
                    </div>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div class="form-group">
                    <label for="invoice_number">{{ $t('purchase.invoiceNumber') }}:</label>
                    <d-input
                      v-if="!parent_id"
                      :readonly="isReadOnly || !!parent_id"
                      v-model="invoice_number" />
                    <div v-else>
                      <template v-if="parent">
                        {{ parent.invoice_number }}
                      </template>
                      <template v-else>
                        &mdash;
                      </template>
                    </div>
                  </div>
                </d-col>

                <d-col cols="4">
                  <label>
                    {{ $t('purchase.invoiceDate') }}:
                  </label>

                  <validation-provider
                    v-if="!isReadOnly && !parent_id"
                    :rules="invoice_number ? 'required' : ''"
                    name="invoice-date"
                    v-slot="{ errors }">
                    <d-datepicker
                      v-model="invoice_date"
                      :monday-first="true"
                      format="yyyy-MM-dd"
                      :input-class="{ 'form-control': true, 'is-invalid': errors.length }"
                      :placeholder="$t('ui.chooseDate')"
                      :language="translationsForDatePicker" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>

                  <div v-else>
                    {{ invoiceDateAsString }}
                  </div>
                </d-col>
              </d-row>

              <d-row v-if="subShipperSelected && (!parent_id || (parent_id && parent))">
                <d-col cols="4">
                  <div class="form-group">
                    <label for="inputInvoice">{{ $t('purchase.subInvoiceFile') }}:</label>
                    <div>
                      <template v-if="parent_id">
                        <a
                          v-if="parent && parent.sub_file && typeof parent.sub_file === 'string'"
                          href="#"
                          @click.prevent="downloadSubInvoice()">
                          <template v-if="!downloadingSubInvoice">
                            <i class="material-icons">request_quote</i> {{ $t('purchase.downloadInvoice') }}
                          </template>
                          <template v-if="downloadingSubInvoice">{{ $t('ui.downloadInProgress') }}&hellip;</template>
                        </a>

                        <span v-if="parent_id && (!parent.file || typeof parent.file !== 'string')">
                          &mdash;
                        </span>
                      </template>
                      <template v-else>
                        <a
                          v-if="sub_file && typeof sub_file === 'string'"
                          href="#"
                          @click.prevent="downloadSubInvoice()">
                          <template v-if="!downloadingSubInvoice">
                            <i class="material-icons">request_quote</i> {{ $t('purchase.downloadInvoice') }}
                          </template>
                          <template v-if="downloadingSubInvoice">{{ $t('ui.downloadInProgress') }}&hellip;</template>
                        </a>

                        <span v-if="parent_id && (!file || typeof file !== 'string')">
                          &mdash;
                        </span>
                      </template>

                      <a
                        v-if="!parent_id && sub_file && typeof sub_file === 'string' && !isReadOnly"
                        href="#"
                        @click.prevent="removeSubInvoice"
                        class="text-danger ml-3">
                        <small>{{ $t('purchase.removeInvoice') }}</small>
                      </a>

                      <file-input
                        v-if="!parent_id && (!sub_file || typeof sub_file !== 'string') && !isReadOnly"
                        :id="'inputSubAttachment'"
                        :label="$t('purchase.selectInvoiceFile')"
                        :onChange="setSubInvoice" />
                    </div>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div class="form-group">
                    <label for="sub_invoice_number">{{ $t('purchase.subInvoiceNumber') }}:</label>
                    <d-input
                      v-if="!parent_id"
                      :readonly="isReadOnly"
                      v-model="sub_invoice_number" />
                    <div v-else>
                      <template v-if="parent">
                        {{ parent.sub_invoice_number }}
                      </template>
                      <template v-else>
                        &mdash;
                      </template>
                    </div>
                  </div>
                </d-col>

                <d-col cols="4">
                  <label>
                    {{ $t('purchase.subInvoiceDate') }}:
                  </label>

                  <validation-provider
                    v-if="!isReadOnly && !parent_id"
                    :rules="sub_invoice_number ? 'required' : ''"
                    name="sub-invoice-date"
                    v-slot="{ errors }">
                    <d-datepicker
                      v-model="sub_invoice_date"
                      :monday-first="true"
                      format="yyyy-MM-dd"
                      :input-class="{ 'form-control': true, 'is-invalid': errors.length }"
                      :placeholder="$t('ui.chooseDate')"
                      :language="translationsForDatePicker" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>

                  <div v-else>
                    {{ subInvoiceDateAsString }}
                  </div>
                </d-col>
              </d-row>

              <div class="form-group">
                <label for="description">{{ $t('ui.comments') }}:</label>
                <d-form-textarea
                  v-model="description"
                  :value.prop="description"
                  :readOnly="isReadOnly"
                  :placeholder="$t('purchase.addCommentsToOrder')"
                  :rows="6" />
              </div>

              <div class="form-group">
                <label for="inputOrder">
                  {{ $t('attachment.filesToAttach') }}:
                </label>

                <div
                  v-for="(file, index) of files"
                  :key="'file-to-attach-' + index"
                  class="mt-2">
                  <span class="pr-3">{{ file.file }}</span>

                  <a
                    v-if="file.file && typeof file.file === 'string'"
                    href="#"
                    :class="{ 'is-disabled': !!downloadingFile }"
                    @click.prevent="downloadFile(file.id)">
                    <template v-if="downloadingFile !== file.id">
                      <i class="material-icons">cloud_download</i> {{ $t('purchase.downloadFile') }}
                    </template>
                    <template v-if="downloadingFile === file.id">{{ $t('ui.downloadInProgress') }}&hellip;</template>
                  </a>

                  <a
                    v-if="['.jpg', '.png'].indexOf(file.file.substr(-4)) > -1 || ['.jpeg', '.webp'].indexOf(file.file.substr(-5)) > -1 || file.file.substr(-4) === '.pdf'"
                    href="#"
                    @click.prevent="downloadFile(file.id, true, file.file.substr(-4) === '.pdf')"
                    class="ml-1 mr-1">
                    <i class="material-icons text-primary">search</i>
                  </a>

                  <a
                    v-if="file.file && typeof file.file === 'string' && !isReadOnly"
                    href="#"
                    @click.prevent="deleteUploadedFile(index, file.id)"
                    :class="{ 'is-disabled': !!deletingFile }"
                    class="text-danger ml-3">
                    <template v-if="deletingFile !== file.id">
                      <i class="material-icons">delete</i> {{ $t('purchase.removeFile') }}
                    </template>
                    <template v-if="deletingFile === file.id">{{ $t('purchase.removeFileInProgress') }}&hellip;</template>
                  </a>

                  <div
                    v-if="!file.file"
                    class="d-flex">
                    <file-input
                      v-if="!file || typeof file !== 'string' && !isReadOnly"
                      :id="'inputOrderAttachment-' + index"
                      :key="'attached-file-' + index"
                      :label="$t('attachment.chooseFileToAttachOptional')"
                      :onChange="(e) => setFile(e, index)" />

                    <d-button
                      v-if="!file || typeof file !== 'string' && !isReadOnly"
                      theme="danger"
                      :outline="true"
                      class="text-nowrap ml-2"
                      @click.prevent="removeFile(index)">
                      {{ $t('purchase.removeFile') }}
                    </d-button>
                  </div>
                </div>
              </div>

              <d-button
                v-if="!isReadOnly"
                theme="success"
                :outline="true"
                @click.prevent="files.push('')">
                {{ $t('attachment.addAttachemnt') }}
              </d-button>
            </d-tab>

            <!-- Zamówienie -->
            <d-tab :title="$t('ui.order')">
              <purchase-creator
                ref="purchase-creator"
                :status="status"
                :readOnly="isReadOnly"
                :availableProducts="availableProducts"
                :productProperties="productProperties"
                :productPrices="simplifiedProductPrices"
                :purchaseSetUpInProgress="purchaseSetUpInProgress"
                :useSewingRoomIntegration="useSewingRoomIntegration" />
            </d-tab>

            <!-- Szwalnia - logi integracji -->
            <d-tab
              :disabled="!sewing_room_id && !sub_sewing_room_id"
              :key="'sewing-room-logs-tab'"
              :title="$t('purchase.sewingRoomLogs')">
              <d-alert
                v-if="!sewing_room_logs.length"
                show
                theme="warning">
                {{ $t('purchase.noSewingRoomLogs') }}
              </d-alert>

              <table
                v-if="sewing_room_logs.length"
                class="table mt-0 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0">{{ $t('status.status') }}</th>
                    <th scope="col" class="border-0">{{ $t('purchase.customerType') }}</th>
                    <th scope="col" class="border-0">{{ $t('order.creationDate') }}</th>
                    <th scope="col" class="border-0">{{ $t('order.modificationDate') }}</th>
                    <th scope="col" class="border-0">{{ $t('purchase.description') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(logItem, index) of sewing_room_logs"
                    :key="'status-log-' + index">
                    <td class="align-middle">
                      {{ getSewingRoomLogName(logItem.synchronization_type) }}
                    </td>
                    <td class="align-middle">
                      <template v-if="logItem.customer_type === 'customer'">
                        {{ $t('purchase.customerTypeCustomer') }}
                      </template>
                      <template v-if="logItem.customer_type === 'subcustomer'">
                        {{ $t('purchase.customerTypeSubCustomer') }}
                      </template>
                    </td>
                    <td class="align-middle">
                      {{ logItem.created_at }}
                    </td>
                    <td class="align-middle">
                      {{ logItem.updated_at }}
                    </td>
                    <td class="align-middle">
                      <template v-if="logItem.synchronization_type === 'SYNCHRONIZE'">
                        {{ $t('purchase.sewingRoomSynchronized') }}
                      </template>
                      <template v-if="logItem.synchronization_type === 'STATUS'">
                        {{ $t('purchase.sewingRoomStatusChanged') }}
                        <span :class="'text-' + sewingRoomStatus[logItem.sewing_room_status].style">
                          {{ sewingRoomStatus[logItem.sewing_room_status].text[prefferedLanguage] }}
                        </span>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-tab>

            <!-- Zmiany statusów -->
            <d-tab
              :key="'status-logs-tab'"
              :title="$t('purchase.statusLogs')">
              <d-alert
                v-if="!statuses.length"
                show
                theme="warning">
                {{ $t('purchase.noSewingRoomLogs') }}
              </d-alert>

              <table
                v-if="statuses.length"
                class="table mt-0 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0">{{ $t('status.status') }}</th>
                    <th scope="col" class="border-0">{{ $t('order.creationDate') }}</th>
                    <th scope="col" class="border-0">{{ $t('order.modificationDate') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(logItem, index) of statuses"
                    :key="'status-state-log-' + index">
                    <td class="align-middle">
                      <d-badge
                        v-if="logItem.status === 'NEW'"
                        theme="primary">
                        {{ $t('purchase.newOrder') }}
                      </d-badge>
                      <d-badge
                        v-if="logItem.status === 'WAITING_PAYMENT'"
                        theme="warning">
                        {{ $t('order.pendingPayment') }}
                      </d-badge>
                      <d-badge
                        v-if="logItem.status === 'WAITING_PRODUCTS'"
                        theme="warning">
                        {{ $t('order.waitingPurchaseProducts') }}
                      </d-badge>
                      <d-badge
                        v-if="logItem.status === 'CLOSED'"
                        theme="danger">
                        {{ $t('purchase.closed') }}
                      </d-badge>
                    </td>
                    <td class="align-middle">
                      {{ logItem.created_at }}
                    </td>
                    <td class="align-middle">
                      {{ logItem.updated_at }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-tab>

            <!-- Wysłane powiadomienia -->
            <d-tab
              :key="'status-notifications-tab'"
              :title="$t('purchase.notifications.emailNotifications')">
              <d-alert
                v-if="!emails.length"
                show
                theme="warning">
                {{ $t('purchase.notifications.noEmailNotifications') }}
              </d-alert>

              <purchase-notifications
                :emails="emails" />
            </d-tab>

            <!-- Wysłane powiadomienia -->
            <d-tab
              :key="'relations-tab'"
              :title="$t('purchase.relations')">
              <purchase-relations
                v-if="allDataLoaded"
                ref="purchase-relations"
                :canAssignToParent="can_assign_to_parent"
                :canAssignToThis="can_assign_to_this"
                :parent="parent"
                :parentID="parent_id"
                :purchaseCategory="purchase_category"
                :customerID="pz_for_customer_id"
                :warehouse="warehouse"
                :children="children"
                :isReadOnly="isReadOnly" />
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded || purchaseSetUpInProgress || loadingSupplierPricesInProgress" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>

    <purchase-erp-integration-popup
      v-if="showErpIntegrationPopup"
      :purchaseID="parseInt(this.$route.params.id, 10)" />
  </d-container>
</template>

<script>
import datepickerTranslations from './../../data/datepicker-translations.js';
import FormUtils from './../../utils/FormUtils.js';
import PurchaseCreator from './PurchaseCreator.vue';
import PurchaseErpIntegrationPopup from './PurchaseErpIntegrationPopup.vue';
import PurchaseNotifications from './PurchaseNotifications.vue';
import PurchaseRelations from './PurchaseRelations.vue';
import PurchaseSelectContact from './PurchaseSelectContact.vue';
import PurchaseSelectShipper from './PurchaseSelectShipper.vue';
import PurchaseStatus from './PurchaseStatus.vue';
import Vue from 'vue';
import sewingRoomStatuses from './../../data/sewing-room-status.js';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'purchase',
  components: {
    'purchase-creator': PurchaseCreator,
    'purchase-erp-integration-popup': PurchaseErpIntegrationPopup,
    'purchase-notifications': PurchaseNotifications,
    'purchase-relations': PurchaseRelations,
    'purchase-select-contact': PurchaseSelectContact,
    'purchase-select-shipper': PurchaseSelectShipper,
    'purchase-status': PurchaseStatus
  },
  computed: {
    isReadOnly () {
      return (this.status === 'CLOSED' && !this.sewing_room_id);
    },
    isEditMode () {
      return !!this.$route.params.id;
    },
    allDataLoaded () {
      if (this.isEditMode) {
        return this.loadedData.join(',') === 'customer,product-properties,products,purchase';
      }

      return this.loadedData.join(',') === 'product-properties,products';
    },
    invoiceDateAsString () {
      let date = this.invoice_date;

      if (this.parent_id) {
        date = this.parent.invoice_date;
      }

      if (!date) {
        return this.$t('ui.lack');
      }

      return date;
    },
    subInvoiceDateAsString () {
      let date = this.sub_invoice_date;

      if (this.parent_id) {
        date = this.parent.sub_invoice_date;
      }

      if (!date) {
        return this.$t('ui.lack');
      }

      return date;
    },
    dateAsString () {
      let date = this.date;

      if (!date) {
        return this.$t('ui.lack');
      }

      if (typeof date === 'object') {
        return date.toISOString().split('T')[0];
      }

      return date;
    },
    validationResults () {
      let errors = this.$refs['purchase-form'].errors;
      let allKeys = Object.keys(errors);
      let errorKeys = [];
      let errorTabs = [];
      let results = [
        ['error-1', ['customer-id']],
        ['error-2', ['product-id-', 'product-price-net-', 'product-vat-rate-', 'product-quantity-']]
      ];

      for (let i = 0; i < allKeys.length; i++) {
        if (errors[allKeys[i]].length) {
          errorKeys.push(allKeys[i]);
        }
      }

      errorKeys = errorKeys.join(',');

      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i][1].length; j++) {
          let key = results[i][1][j];

          if (errorKeys.indexOf(key) > -1) {
            errorTabs.push(results[i][0]);
            break;
          }
        }
      }

      return errorTabs.join(' ');
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    backToReservations () {
      return !!this.$route.params.fromreservations;
    },
    prefferedLanguage () {
      return getPrefferedLanguage();
    },
    sewingRoomStatus () {
      return sewingRoomStatuses;
    },
    simplifiedProductPrices () {
      let output = {};

      for (let i = 0; i < this.productPrices.length; i++) {
        let key = this.productPrices[i].product_id;
        let value = this.productPrices[i].price;
        output[key] = value;
      }

      return output;
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    }
  },
  watch: {
    loadedProducts () {
      this.$bus.$emit('purchase-data-fetch-success', 'products');
    },
    loadedPurchase () {
      this.$bus.$emit('purchase-data-fetch-success', 'purchase');
    },
    loadedProductProperties () {
      this.$bus.$emit('purchase-data-fetch-success', 'product-properties');
    }
  },
  data() {
    return {
      customer_id: null,
      customer_name: null,
      contact_id: null,
      contact_name: null,
      created_at: null,
      collections: [],
      emails: [],
      erp_number: '',
      file: null,
      sub_file: null,
      files: [],
      invoice_number: '',
      invoice_date: null,
      sub_invoice_number: '',
      sub_invoice_date: null,
      description: '',
      status: '',
      sub_customer_id: null,
      sub_customer_name: '',
      date: null,
      can_send_invoice_email: false,
      sewing_room_synchronized_at: null,
      sewing_room_id: null,
      sewing_room_logs: [],
      sewing_room_status: null,
      sub_sewing_room_synchronized_at: null,
      sub_sewing_room_id: null,
      sub_sewing_room_status: null,
      received_at: null,
      suborder_received_at: null,
      order_name: '',
      orders_count: 0,
      sewing_room_url: '',
      sub_sewing_room_url: '',
      pz_for: null,
      pz_for_customer_id: null,
      exclude_from_erp_integration: null,
      erp_optima_updated_at: null,
      excludeFromErpInProgress: false,
      includeToErpInProgress: false,
      // Data
      products: [],
      productPrices: [],
      productProperties: [],
      availableProducts: [],
      statuses: [],
      warehouse: null,
      purchase_category: null,
      download_label_url: '',
      // relations
      can_assign_to_parent: false,
      can_assign_to_this: false,
      parent: null,
      parent_id: 0,
      children: [],
      // temporary data
      selectedCustomer: null,
      selectedPersonData: null,
      // flags
      loadError: false,
      loadedData: [],
      loadedPurchase: false,
      loadedProductProperties: false,
      loadedProducts: false,
      purchaseSetUpInProgress: false,
      downloadingInvoice: false,
      downloadingSubInvoice: false,
      downloadingFile: false,
      closeInProgress: false,
      forceCloseInProgress: false,
      useSewingRoomIntegration: false,
      deletingFile: false,
      submitInProgress: false,
      sendPurchaseEmailInProgress: false,
      deadlineIsSet: false,
      shipperLoadedInSelect: false,
      loadingSupplierPricesInProgress: false,
      loadedSupplierPrices: false,
      downloadInProgress: false,
      parentPurchaseReset: false,
      subShipperSelected: false,
      showErpIntegrationPopup: false
    };
  },
  mounted () {
    if (this.isEditMode) {
      this.loadData();
    }

    this.loadAdditionalData();
    this.$bus.$on('purchase-reload', this.loadData);
    this.$bus.$on('purchase-data-fetch-error', this.dataFetchError);
    this.$bus.$on('purchase-data-fetch-success', this.dataFetchSuccess);
    this.$bus.$on('purchase-select-shipper-sewing-room-integration-change', this.setSewingRoomIntegrationState);
    this.$bus.$on('purchase-reload-product-properties', this.loadProductProperties);
    this.$bus.$on('purchase-select-shipper-changed', this.shipperChanged);
    this.$bus.$on('purchase-select-sub-shipper-changed', this.subShipperChanged);
    this.$bus.$on('purchase-parent-has-been-reset', this.parentPurchaseReseted);
    this.$bus.$on('purchase-select-shipper-set-warehouse', this.setWarehouse);
    this.$bus.$on('purchase-select-shipper-set-purchase-category', this.setPurchaseCategory);
    this.$bus.$on('purchase-select-change', this.setParentPurchaseID);
    this.$bus.$on('show-purchase-erp-integration-popup', this.openErpIntegrationPopup);
    this.$bus.$on('close-purchase-erp-integration-popup', this.closeErpIntegrationPopup);
  },
  methods: {
    validateForm (saveMode = 'normal') {
      if (this.isReadOnly) {
        if (saveMode === 'close') {
          this.closePurchase();
        } else if (saveMode === 'forceClose') {
          this.forceClosePurchase();
        }
      } else {
        FormUtils.validate(this.$refs['purchase-form'], this.submitForm.bind(this, saveMode));
      }
    },
    loadData () {
      this.purchaseSetUpInProgress = true;

      FormUtils.loadItemData(this, {
        endpointBase: '/purchases/item/',
        instanceFields: ['created_at', 'customer_id', 'customer_name', 'sub_customer_id', 'sub_customer_name', 'contact_id', 'contact_name', 'collections', 'emails', 'file', 'sub_file', 'files', 'invoice_number', 'invoice_date', 'sub_invoice_number', 'sub_invoice_date', 'description', 'status', 'products', 'sewing_room_synchronized_at', 'sewing_room_id', 'sewing_room_status', 'sub_sewing_room_synchronized_at', 'sub_sewing_room_id', 'sub_sewing_room_status', 'sewing_room_logs', 'can_send_invoice_email', 'statuses', 'erp_number', 'warehouse', 'purchase_category', 'can_assign_to_parent', 'can_assign_to_this', 'parent', 'children', 'parent_id', 'download_label_url', 'received_at', 'suborder_received_at', 'order_name', 'orders_count', 'sewing_room_url', 'sub_sewing_room_url', 'pz_for', 'pz_for_customer_id', 'exclude_from_erp_integration', 'erp_optima_updated_at'],
        loadedKey: 'loadedPurchase',
        errorKey: 'loadError',
        params: {
          with: [
            'purchaseProducts',
            'sewingRoomLogs',
            'statuses',
            'purchaseCollections'
          ]
        },
        successAction: (response) => {
          this.$refs['purchase-shipper'].loadShipper(this.customer_id);
          this.$refs['purchase-subshipper'].loadShipper(this.sub_customer_id);
          this.$refs['purchase-contact'].setContactID(this.contact_id);
          this.date = response.date ? new Date(response.date) : null;
          this.loadSupplierPrices(this.customer_id);

          if (!this.date) {
            this.deadlineIsSet = false;
          } else {
            this.deadlineIsSet = true;
          }

          this.setUpPurchase();
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'availableProducts',
        errorKey: 'loadError',
        params: {
          where: {
            available_in_purchase: 1
          }
        }
      });

      this.loadProductProperties();
    },
    loadProductProperties () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/sewing-room/items',
        loadedKey: 'loadedProductProperties',
        outputKey: 'productProperties',
        errorKey: 'loadError'
      });
    },
    loadSupplierPrices (customerID = false) {
      if (!customerID) {
        customerID = this.$refs['purchase-shipper'].customer_id;
      }

      this.loadedSupplierPrices = false;

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/customers/prices/items',
        loadedKey: 'loadedSupplierPrices',
        outputKey: 'productPrices',
        errorKey: 'loadError',
        params: {
          where: {
            customer_id: customerID
          },
          return: 'get'
        },
        successAction: () => {
          this.loadingSupplierPricesInProgress = false;
        }
      });
    },
    submitForm (saveMode) {
      this.submitInProgress = true;
      let dataToSend = this.prepareFormData();
      let filesToSend = this.prepareFormFiles();

      if (this.isEditMode) {
        FormUtils.submit({
          $http: this.$http,
          endpoint: '/purchases/update/' + parseInt(this.$route.params.id, 10),
          formData: dataToSend,
          successTitle: this.$t('purchase.editOrderSuccessTitle'),
          successText: this.$t('purchase.editOrderSuccessText'),
          errorTitle: this.$t('ui.errorOccured'),
          errorText: this.$t('purchase.editOrderErrorText'),
          successAction: () => {
            if (saveMode === 'normal') {
              this.$bus.$emit('purchase-reload');
            } else if (saveMode === 'close') {
              this.closePurchase();
            } else if (saveMode === 'forceClose') {
              this.forceClosePurchase();
            }

            this.submitInProgress = false;
            this.parentPurchaseReset = false;
          },
          failAction: () => {
            this.submitInProgress = false;
          }
        }, filesToSend);
      } else {
        FormUtils.submit({
          $http: this.$http,
          endpoint: '/purchases/store',
          formData: dataToSend,
          successTitle: this.$t('purchase.addOrderSuccessTitle'),
          successText: this.$t('purchase.addOrderSuccessText'),
          errorTitle: this.$t('ui.errorOccured'),
          errorText: this.$t('purchase.addOrderErrorText'),
          successAction: () => {
            this.submitInProgress = false;
            this.$router.push('/zakupy');
          },
          failAction: () => {
            this.submitInProgress = false;
          }
        }, filesToSend);
      }
    },
    prepareFormData () {
      let formData = {
        customer_id: this.$refs['purchase-shipper'].customer_id,
        sub_customer_id: this.$refs['purchase-subshipper'].customer_id,
        contact_id: this.$refs['purchase-contact'].contact_id,
        description: this.description,
        date: this.date ? [this.date.getFullYear(), this.date.getMonth() + 1, this.date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') : null,
        products: this.$refs['purchase-creator'].prepareProductsData(),
        warehouse: this.warehouse,
        purchase_category: this.purchase_category
      };

      if (!this.parent_id) {
        formData.invoice_number = this.invoice_number;
        formData.invoice_date = this.invoice_date;

        if (this.$refs['purchase-subshipper'].customer_id) {
          formData.sub_invoice_number = this.sub_invoice_number;
          formData.sub_invoice_date = this.sub_invoice_date;
        } else {
          formData.sub_invoice_number = '';
          formData.sub_invoice_date = '';
        }
      }

      if (!this.isEditMode || (this.isEditMode && this.orders_count === 0)) {
        formData.order_name = this.order_name;
      }

      if (this.file === '') {
        formData.file = '';
      }

      if (!this.$refs['purchase-subshipper'].customer_id || this.sub_file === '') {
        formData.sub_file = '';
      }

      if (this.$refs['purchase-relations']) {
        formData.parent_id = this.$refs['purchase-relations'].parent_id;
      }

      return formData;
    },
    prepareFormFiles () {
      let files = [];

      if (!this.parent_id && this.file) {
        files.push({
          key: 'file',
          value: this.file
        });
      }

      if (!this.parent_id && this.$refs['purchase-subshipper'].customer_id && this.sub_file) {
        files.push({
          key: 'sub_file',
          value: this.sub_file
        });
      }

      if (this.files) {
        for (let i = 0; i < this.files.length; i++) {
          if (!this.files[i].file) {
            files.push({
              key: 'files[' + i + ']',
              value: this.files[i]
            });
          }
        }
      }

      return files;
    },
    dataFetchError () {
      this.loadError = true;
    },
    dataFetchSuccess (dataKey) {
      if (this.loadedData.indexOf(dataKey) === -1) {
        this.loadedData.push(dataKey);
        this.loadedData.sort();
      }
    },
    setUpPurchase () {
      this.$refs['purchase-creator'].setUpProducts(this.products);

      Vue.nextTick(() => {
        this.purchaseSetUpInProgress = false;
      });
    },
    setFile (e, index) {
      this.files[index] = e.target.files[0];
    },
    removeFile (index) {
      this.files.splice(index, 1);
    },
    deleteUploadedFile (index, fileID) {
      this.deletingFile = fileID;

      this.$http.post('/purchases/files/delete/' + fileID).then(response => {
        if (response.data.success) {
          Vue.swal({
            title: this.$t('purchase.fileDeletedTitle'),
            html: this.$t('purchase.fileDeletedDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.files.splice(index, 1);
          this.deletingFile = false;
        } else {
          Vue.swal({
            title: this.$t('ui.errorOccured'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
          this.deletingFile = false;
        }
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccured'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.deletingFile = false;
      });
    },
    downloadFile (fileID, showPreview = false, showPDF = false) {
      this.downloadingFile = fileID;

      this.$http.post('/purchases/files/download/' + fileID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

        if (showPreview) {
          if (showPDF) {
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 5000);
          } else {
            this.$bus.$emit('image-preview-popup-show', url);
          }

          this.downloadingFile = false;
          return;
        }

        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingFile = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingFile = false;
      });
    },
    setInvoice (e) {
      this.file = e.target.files[0];
    },
    removeInvoice () {
      this.file = '';
    },
    downloadInvoice () {
      this.downloadingInvoice = true;
      let orderID = parseInt(this.$route.params.id, 10);

      if (this.parent_id) {
        orderID = parseInt(this.parent_id, 10);
      }

      this.$http.post('/purchases/download-file/' + orderID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingInvoice = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingInvoice = false;
      });
    },
    setSubInvoice (e) {
      this.sub_file = e.target.files[0];
    },
    removeSubInvoice () {
      this.sub_file = '';
    },
    downloadSubInvoice () {
      this.downloadingSubInvoice = true;
      let orderID = parseInt(this.$route.params.id, 10);

      if (this.parent_id) {
        orderID = parseInt(this.parent_id, 10);
      }

      this.$http.post('/purchases/download-sub-file/' + orderID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingSubInvoice = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingSubInvoice = false;
      });
    },
    closePurchase () {
      this.closeInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/purchases/close/' + this.id,
        formData: {},
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('purchase.orderCloseErrorTextTryAgain'),
        successAction: (response) => {
          this.closeInProgress = false;

          if (response.success) {
            this.$router.push('/zakupy');
          } else {
            Vue.swal({
              title: this.$t('purchase.orderCloseErrorTitle'),
              html: this.$t('purchase.orderCloseErrorText'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.closeInProgress = false;
        }
      });
    },
    forceClosePurchase () {
      this.forceCloseInProgress = true;

      Vue.swal({
        title: this.$t('purchase.forceOrderCloseConfirmTitle'),
        text: this.$t('purchase.forceOrderCloseConfirmText'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('purchase.yesClose'),
        cancelButtonText: this.$t('ui.cancel'),
      }).then((result) => {
        if (result.value) {
          FormUtils.submitInBackground({
            $http: this.$http,
            endpoint: '/purchases/force-close/' + this.id,
            formData: {},
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('purchase.forceOrderCloseErrorText'),
            successAction: (response) => {
              this.forceCloseInProgress = false;

              if (response.success) {
                this.$router.push(this.backToReservations ? '/zakupy/rezerwacje' : '/zakupy');
              }
            },
            failAction: () => {
              this.forceCloseInProgress = false;
            }
          });
        } else {
          this.forceCloseInProgress = false;
        }
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccured'),
          html: this.$t('purchase.forceOrderCloseErrorText'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.forceCloseInProgress = false;
      });
    },
    setSewingRoomIntegrationState (newValue) {
      this.useSewingRoomIntegration = newValue;
    },
    getSewingRoomLogName (status) {
      if (status === 'SYNCHRONIZE') {
        return this.$t('purchase.sewingRoomLogTypeSynchronize');
      }

      if (status === 'STATUS') {
        return this.$t('purchase.sewingRoomLogTypeStatus');
      }

      return '-';
    },
    sendPurchaseEmail (receiverType) {
      this.sendPurchaseEmailInProgress = true;

      this.$http.post('/purchases/send-invoice-email/' + parseInt(this.$route.params.id, 10), {
        receiver_type: receiverType
      }).then(response => {
        if (response.data.success) {
          Vue.swal({
            title: this.$t('purchase.notificationSentTitle'),
            html: this.$t('purchase.notificationSentDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.$bus.$emit('purchase-reload');
        } else {
          Vue.swal({
            title: this.$t('ui.errorOccuredDuringSending'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }

        this.sendPurchaseEmailInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringSending'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        this.sendPurchaseEmailInProgress = false;
      });
    },
    shipperChanged (value) {
      if (this.isEditMode && !this.shipperLoadedInSelect) {
        this.shipperLoadedInSelect = true;
        return;
      }

      if (value !== null) {
        this.loadingSupplierPricesInProgress = true;
        this.loadSupplierPrices();

        if (this.$refs['purchase-creator'] && this.$refs['purchase-creator'].products.length) {
          Vue.swal({
            title: this.$t('purchase.supplierChanged'),
            html: this.$t('purchase.supplierChangedDesc'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      }
    },
    subShipperChanged (value) {
      this.subShipperSelected = !!value;
    },
    downloadLabel () {
      this.downloadInProgress = true;

      this.$http.post(this.download_label_url, null, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('shipments.labelFilePrefix');
        link.setAttribute('download', filenamePrefix + parseInt(this.$route.params.id, 10) + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadInProgress = false;
      });
    },
    parentPurchaseReseted (value) {
      this.parentPurchaseReset = value;
    },
    setWarehouse (newValue) {
      if (this.isEditMode) {
        return;
      }

      this.warehouse = newValue;
    },
    setPurchaseCategory (newValue) {
      if (this.isEditMode) {
        return;
      }

      this.purchase_category = newValue;
    },
    setParentPurchaseID (newID) {
      this.parent_id = newID;
    },
    closeErpIntegrationPopup () {
      Vue.set(this, 'showErpIntegrationPopup', false);
    },
    openErpIntegrationPopup () {
      Vue.set(this, 'showErpIntegrationPopup', true);
    },
    excludeFromERP () {
      if (this.excludeFromErpInProgress) {
        return;
      }

      this.excludeFromErpInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/purchases/exclude-from-erp-integration/' + parseInt(this.$route.params.id, 10),
        formData: {
          exclude_from_erp_integration: 1
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          this.excludeFromErpInProgress = false;

          if (response.success) {
            Vue.swal({
              title: this.$t('order.erp.excludeFailedTitle'),
              html: this.$t('order.erp.excludeFailedDesc'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.exclude_from_erp_integration = true;
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.actionFailedTryAgain'),
              type: 'error',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.excludeFromErpInProgress = false;
        }
      });
    },
    includeToERP () {
      if (this.includeToErpInProgress) {
        return;
      }

      this.includeToErpInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/purchases/exclude-from-erp-integration/' + parseInt(this.$route.params.id, 10),
        formData: {
          exclude_from_erp_integration: 0
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          this.includeToErpInProgress = false;

          if (response.success) {
            Vue.swal({
              title: this.$t('order.erp.includeFailedTitle'),
              html: this.$t('order.erp.includeFailedDesc'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.exclude_from_erp_integration = false;
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.actionFailedTryAgain'),
              type: 'error',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.includeToErpInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('purchase-reload', this.loadData);
    this.$bus.$off('purchase-data-fetch-error', this.dataFetchError);
    this.$bus.$off('purchase-data-fetch-success', this.dataFetchSuccess);
    this.$bus.$off('purchase-select-shipper-sewing-room-integration-change', this.setSewingRoomIntegrationState);
    this.$bus.$off('purchase-reload-product-properties', this.loadProductProperties);
    this.$bus.$off('purchase-select-shipper-changed', this.shipperChanged);
    this.$bus.$off('purchase-select-sub-shipper-changed', this.subShipperChanged);
    this.$bus.$off('purchase-parent-has-been-reset', this.parentPurchaseReseted);
    this.$bus.$off('purchase-select-shipper-set-warehouse', this.setWarehouse);
    this.$bus.$off('purchase-select-shipper-set-purchase-category', this.setPurchaseCategory);
    this.$bus.$off('purchase-select-change', this.setParentPurchaseID);
    this.$bus.$off('show-purchase-erp-integration-popup', this.openErpIntegrationPopup);
    this.$bus.$off('close-purchase-erp-integration-popup', this.closeErpIntegrationPopup);
  }
};
</script>

<style lang="scss" scoped>
.submit-in-progress {
  opacity: .66;
  pointer-events: none;
}

.form-control.is-select {
  background-image: none!important;
  padding: 0!important;
}

.is-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.order-status-wrapper {
  width: 400px;

  & > div {
    display: flex;
    width: 100%;
  }

  /deep/ .v-select {
    width: 100%;

    .vs__dropdown-toggle {
      border: 1px solid #e1e5eb;
    }
  }
}

.purchase-info {
  border: 1px solid #ccc;
  border-radius: 5px;
  margin: 1rem 0 0 0;
  padding: .5rem;

  &-content {
    span {
      display: block;
      font-size: 12px;
      font-weight: normal;
    }
  }
}
</style>
